import { ApiResponse, ChangelogEntry, Cloud, InferenceProfile, ModelRecord, ModelRow, RegionEntry, SortDir, SortKey } from '../types';
import { countryFromSelector, destinationsWithinScope, regionsInCountry, ResidencyScope } from './regions';

/** A model's true identity is cloud + modelId, not modelId alone — the same
 *  ID string is never expected to collide across clouds, but two different
 *  clouds' listings must never silently merge into one row if it ever did. */
export function rowKey(m: { cloud: Cloud; modelId: string }): string {
  return `${m.cloud}|${m.modelId}`;
}

/** Absent in payloads written before multi-cloud support existed. */
export function resolveCloud(m: ModelRecord): Cloud {
  return m.cloud ?? 'aws';
}

/** Combines the AWS payload with an optional secondary cloud's payload into
 *  one ApiResponse. Every derived field (region count, listing count,
 *  region labels) is recomputed from the union rather than trying to
 *  reconcile the two source payloads' own precomputed values. `updatedAt`
 *  takes the OLDER of the two timestamps — each cloud refreshes on its own
 *  independent schedule, and staleness should reflect whichever is behind. */
export function mergeApiResponses(primary: ApiResponse, secondary: ApiResponse | null): ApiResponse {
  if (!secondary) return primary;

  const models = [...primary.models, ...secondary.models];
  const inferenceProfiles = [...(primary.inferenceProfiles ?? []), ...(secondary.inferenceProfiles ?? [])];
  const regionLabels = { ...secondary.regionLabels, ...primary.regionLabels };
  const updatedAt = new Date(primary.updatedAt) < new Date(secondary.updatedAt)
    ? primary.updatedAt
    : secondary.updatedAt;

  return {
    updatedAt,
    regionCount: new Set(models.map(m => m.region)).size,
    modelCount: models.length,
    regionLabels,
    models,
    inferenceProfiles,
  };
}

/** Merges both clouds' changelog entries into one newest-first list. Each
 *  cloud writes its own file with no knowledge of the other, so `cloud` is
 *  stamped here per source rather than trusted from the payload — older
 *  entries written before multi-cloud support won't have it set at all. */
export function mergeChangelogs(aws: ChangelogEntry[], azure: ChangelogEntry[]): ChangelogEntry[] {
  const tagged = [
    ...aws.map(e => ({ ...e, cloud: 'aws' as Cloud })),
    ...azure.map(e => ({ ...e, cloud: 'azure' as Cloud })),
  ];
  return tagged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function buildRows(data: ApiResponse): ModelRow[] {
  // Inference profiles (cross-region routing) are an AWS Bedrock-specific
  // mechanism — never attach them to a non-AWS row.
  const profilesByModel = new Map<string, InferenceProfile[]>();
  for (const p of data.inferenceProfiles ?? []) {
    if (!profilesByModel.has(p.baseModelId)) profilesByModel.set(p.baseModelId, []);
    profilesByModel.get(p.baseModelId)!.push(p);
  }

  const index = new Map<string, ModelRow>();
  for (const m of data.models) {
    const cloud = resolveCloud(m);
    const key = rowKey({ cloud, modelId: m.modelId });
    if (!index.has(key)) {
      index.set(key, {
        modelId: m.modelId,
        modelName: m.modelName,
        providerName: m.providerName,
        cloud,
        inputModalities: m.inputModalities,
        outputModalities: m.outputModalities,
        inferenceTypesSupported: [],
        isCri: false,
        regions: [],
        streamingSupported: null,
        inferenceProfiles: cloud === 'aws' ? (profilesByModel.get(m.modelId) ?? []) : [],
      });
    }
    const row = index.get(key)!;
    if (!row.regions.some(re => re.region === m.region)) {
      row.regions.push({
        region: m.region,
        isCri: m.isCri,
        modelLifecycleStatus: m.modelLifecycleStatus,
        inferenceTypesSupported: m.inferenceTypesSupported,
      });
    }
    if (m.isCri) row.isCri = true;
    // Union across all regions this model appears in — a single region's
    // tiers aren't representative of the whole row.
    for (const t of m.inferenceTypesSupported) {
      if (!row.inferenceTypesSupported.includes(t)) row.inferenceTypesSupported.push(t);
    }
    if (m.responseStreamingSupported != null) {
      row.streamingSupported = (row.streamingSupported ?? false) || m.responseStreamingSupported;
    }
  }
  return Array.from(index.values());
}

const TIER_LABELS: Record<string, string> = {
  ON_DEMAND: 'On-demand',
  PROVISIONED: 'Provisioned',
  Standard: 'Standard',
  ProvisionedManaged: 'Provisioned',
};

/** Human label for one or more raw deployment-tier codes (AWS ON_DEMAND/
 *  PROVISIONED, Azure Standard/ProvisionedManaged) — one shared table since
 *  the raw codes never collide across clouds. Unknown codes pass through
 *  as-is so a new SKU tier doesn't silently disappear. */
export function tierLabel(tiers: string[]): string {
  return [...new Set(tiers.map(t => TIER_LABELS[t] ?? t))].join(' + ');
}

/** Formats a region list for changelog display, appending each region's
 *  deployment tier in parens when known — e.g. "us-east-1 (On-demand),
 *  eu-west-2 (Provisioned)". Falls back to the bare region id when
 *  regionTiers is absent or has no entry for it (older changelog entries
 *  computed before this field was collected). */
export function regionsWithTiers(regions: string[], regionTiers?: Record<string, string[]>): string {
  return regions
    .map(r => {
      const label = tierLabel(regionTiers?.[r] ?? []);
      return label ? `${r} (${label})` : r;
    })
    .join(', ');
}

export type LifecycleStatus = 'ACTIVE' | 'LEGACY' | 'MIXED';

export function isRegionActive(re: { modelLifecycleStatus: string | null }): boolean {
  return re.modelLifecycleStatus === 'ACTIVE' || re.modelLifecycleStatus == null;
}

/** Summarizes lifecycle status across a set of region entries. A model
 *  reported Active in the region you queried can still be independently
 *  deprecated in another — surfacing "Mixed" rather than silently picking
 *  one region's status is what stops a card from claiming "Active" for a
 *  model that then errors as deprecated when actually called. */
export function lifecycleStatus(regions: { modelLifecycleStatus: string | null }[]): LifecycleStatus {
  if (regions.length === 0) return 'ACTIVE';
  const activeCount = regions.filter(isRegionActive).length;
  if (activeCount === regions.length) return 'ACTIVE';
  if (activeCount === 0) return 'LEGACY';
  return 'MIXED';
}

/** Finds a row by its rowKey. Also accepts a bare modelId (no "cloud|"
 *  prefix) for links generated before clouds existed — those predate any
 *  possibility of a cross-cloud collision, so a plain modelId match is safe. */
export function findRowByKey(rows: ModelRow[], raw: string): ModelRow | undefined {
  const sep = raw.indexOf('|');
  if (sep === -1) return rows.find(r => r.modelId === raw);
  const cloud = raw.slice(0, sep);
  const modelId = raw.slice(sep + 1);
  return rows.find(r => r.cloud === cloud && r.modelId === modelId);
}

export function sortRows(rows: ModelRow[], key: SortKey, dir: SortDir): ModelRow[] {
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (key === 'regions') {
      cmp = a.regions.length - b.regions.length;
    } else if (key === 'isCri') {
      cmp = Number(a.isCri) - Number(b.isCri);
    } else {
      cmp = String(a[key]).localeCompare(String(b[key]));
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  const curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = curr.slice();
  }
  return prev[b.length];
}

/** How many edits a word of this length may be off by and still count as a
 *  typo rather than a different word. Short words get no slack — a 1-char
 *  edit distance on "ai" or "gpt" matches almost anything. */
function fuzzyThreshold(len: number): number {
  if (len <= 3) return 0;
  if (len <= 6) return 1;
  return 2;
}

function searchWords(row: ModelRow): string[] {
  return `${row.modelId} ${row.modelName} ${row.providerName}`
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Fallback for when the query isn't a literal substring anywhere — treats
 *  each space-separated term as needing its own near-match among the row's
 *  words, so a typo in one word of a multi-word query still finds the row. */
function fuzzyMatches(row: ModelRow, query: string): boolean {
  const terms = query.split(/\s+/).filter(Boolean);
  if (terms.length === 0) return false;
  const words = searchWords(row);
  return terms.every(term =>
    words.some(w => levenshtein(term, w) <= fuzzyThreshold(term.length)));
}

export interface Filters {
  search: string;
  provider: string;
  activeOnly: boolean;
  inputModality: string;
  outputModality: string;
}

export function filterRows(rows: ModelRow[], f: Filters): ModelRow[] {
  const q = f.search.toLowerCase().trim();
  return rows.filter(row => {
    if (q) {
      const exact = row.modelId.toLowerCase().includes(q)
        || row.modelName.toLowerCase().includes(q)
        || row.providerName.toLowerCase().includes(q);
      if (!exact && !fuzzyMatches(row, q)) return false;
    }
    if (f.provider && row.providerName !== f.provider) return false;
    // "Active only" hides models that are Legacy everywhere, but keeps ones
    // that are still usable in at least one region (Mixed) — hiding those
    // entirely would be more misleading than the badge showing "Mixed".
    if (f.activeOnly && lifecycleStatus(row.regions) === 'LEGACY') return false;
    if (f.inputModality && !row.inputModalities.includes(f.inputModality)) return false;
    if (f.outputModality && !row.outputModalities.includes(f.outputModality)) return false;
    return true;
  });
}

/** Per-source routing for a profile. Payloads written before sourceRoutes was
 *  collected fall back to the old assumption: every member region can call
 *  the profile and be routed to any member region. */
export function profileRoutes(p: InferenceProfile): Record<string, string[]> {
  if (p.sourceRoutes && Object.keys(p.sourceRoutes).length > 0) return p.sourceRoutes;
  return Object.fromEntries(p.memberRegions.map(r => [r, p.memberRegions]));
}

export interface CriOption {
  profile: InferenceProfile;
  /** destinations reachable when calling this profile from the source region */
  destinations: string[];
}

export interface LocationEntry {
  row: ModelRow;
  /** true when nativeRegions is non-empty — kept as a separate field since
   *  most callers only care about the boolean, not which regions */
  native: boolean;
  /** literal regions (within the selected location) where this row is
   *  natively hosted, with their per-region deployment tier(s). One entry
   *  for a literal-region selector; can be several for a country selector
   *  spanning multiple regions/clouds. */
  nativeRegions: RegionEntry[];
  /** routing options callable from the selected location within the chosen scope */
  criOptions: CriOption[];
}

export interface ProviderGroup<T> {
  provider: string;
  items: T[];
}

/** Clusters already-sorted entries into contiguous provider groups, preserving order. */
export function groupByProvider<T extends { row: ModelRow }>(items: T[]): ProviderGroup<T>[] {
  const groups: ProviderGroup<T>[] = [];
  let current: ProviderGroup<T> | null = null;
  for (const item of items) {
    if (!current || current.provider !== item.row.providerName) {
      current = { provider: item.row.providerName, items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }
  return groups;
}

/** Everything callable from `selector` under the given residency scope.
 *  `selector` is either a literal region or a country selector (see
 *  countrySelector) — a country selector expands to every literal region
 *  within it via allRegions, since CRI routing profiles and native
 *  listings are always keyed by literal region, never by country. */
export function modelsAtLocation(
  rows: ModelRow[],
  selector: string,
  scope: ResidencyScope,
  allRegions: string[],
): LocationEntry[] {
  const country = countryFromSelector(selector);
  const sourceRegions = country ? regionsInCountry(country, allRegions) : [selector];

  const entries: LocationEntry[] = [];
  for (const row of rows) {
    const nativeRegions = row.regions
      .filter(re => !re.isCri && sourceRegions.includes(re.region));

    // Dedupe: a country selector can span several literal source regions,
    // and the same profile+destinations combo can turn up from more than
    // one of them — that's one real routing option, not several.
    const criByKey = new Map<string, CriOption>();
    for (const source of sourceRegions) {
      for (const profile of row.inferenceProfiles) {
        const destinations = profileRoutes(profile)[source];
        if (destinations && destinationsWithinScope(scope, source, destinations)) {
          criByKey.set(`${profile.profileId}|${destinations.join(',')}`, { profile, destinations });
        }
      }
    }
    const criOptions = [...criByKey.values()];

    if (nativeRegions.length > 0 || criOptions.length > 0) {
      entries.push({ row, native: nativeRegions.length > 0, nativeRegions, criOptions });
    }
  }
  // In-region first, then provider/name for stable scanning
  return entries.sort((a, b) =>
    Number(b.native) - Number(a.native)
    || a.row.providerName.localeCompare(b.row.providerName)
    || a.row.modelName.localeCompare(b.row.modelName));
}

export interface RouteGroup {
  /** source regions that share this exact destination set */
  sources: string[];
  destinations: string[];
}

export interface ProfileRouting {
  profile: InferenceProfile;
  groups: RouteGroup[];
}

/** Per-profile routing for the model drawer, with source regions grouped by
 *  identical destination sets so divergent routing stands out. */
export function routesForModel(row: ModelRow): ProfileRouting[] {
  return row.inferenceProfiles.map(profile => {
    const routes = profileRoutes(profile);
    const byDestKey = new Map<string, RouteGroup>();
    for (const source of Object.keys(routes).sort()) {
      const destinations = routes[source];
      const key = destinations.join(',');
      if (!byDestKey.has(key)) byDestKey.set(key, { sources: [], destinations });
      byDestKey.get(key)!.sources.push(source);
    }
    return { profile, groups: [...byDestKey.values()] };
  });
}
